import React, { useState, useEffect } from "react";
import RoleBadgeList from "../RoleBadgeList/RoleBadgeList";
import RoleBadge from "../RoleBadge/RoleBadge";
import requestHandler from "../../../utils/requestHandler";
import strings from "../../../locales/ro.json";
import StandardPopup from "../../../components/Global/StandardPopup/StandardPopup";

import "./ProfileCard.css";

const ProfileCard = ({ profileData, isOwnProfile, isAdmin, authToken, onRolesChanged }) => {
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [showBioModal, setShowBioModal] = useState(false);

  if (!profileData) return null;

  const userRoles = profileData.roles || [];
  const canManageRoles = isAdmin;

  const fetchAvailableRoles = async () => {
    try {
      const allRoles = await requestHandler("roles", "GET", authToken);
      const filtered = allRoles.filter(
        (r) => !userRoles.some((ur) => ur.id === r.id)
      );
      setAvailableRoles(filtered);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const handleAddRole = async (roleId) => {
    const uid = profileData.email.split("@")[0];
    setAssignLoading(true);
    try {
      await requestHandler(`users/${uid}/roles`, "POST", authToken, { role_id: roleId });
      setShowRoleDropdown(false);
      if (onRolesChanged) await onRolesChanged();
    } catch (err) {
      console.error("Error assigning role:", err);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveRole = async (roleId) => {
    const uid = profileData.email.split("@")[0];
    try {
      await requestHandler(`users/${uid}/roles/${roleId}`, "DELETE", authToken);
      if (onRolesChanged) await onRolesChanged();
    } catch (err) {
      console.error("Error removing role:", err);
    }
  };

  const toggleDropdown = () => {
    if (!showRoleDropdown) fetchAvailableRoles();
    setShowRoleDropdown(!showRoleDropdown);
  };

  return (
    <div className="profile-card">
      <div className="profile-image-container">
        {profileData.picture_url ? (
          <img
            className="profile-avatar-img"
            src={profileData.picture_url}
            alt={profileData.name}
          />
        ) : (
          <div className="profile-avatar-placeholder">
            {(profileData.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      <h2 className="profile-name">{profileData.name}</h2>

      <div className="profile-roles-section">
        {!showRoleDropdown ? (
          <RoleBadgeList roles={userRoles} />
        ) : (
          <div className="profile-removable-roles-active">
            {userRoles.map((role) => (
              <div key={role.id} className="profile-removable-badge">
                <RoleBadge name={role.name} color={role.color} />
                <button
                  className="profile-role-remove-btn"
                  onClick={() => handleRemoveRole(role.id)}
                  title={strings.admin.roles.assigner.removeTitle}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {canManageRoles && (
          <div className="profile-role-manager">

            <button
              className="profile-add-role-btn"
              onClick={toggleDropdown}
              title={strings.admin.roles.assigner.addRolePlaceholder}
            >
              {showRoleDropdown ? "✕" : "+"}
            </button>

            {showRoleDropdown && (
              <div className="profile-role-dropdown">
                {availableRoles.length === 0 ? (
                  <span className="profile-role-dropdown-empty">{strings.admin.roles.list.empty}</span>
                ) : (
                  availableRoles.map((role) => (
                    <button
                      key={role.id}
                      className="profile-role-dropdown-item"
                      onClick={() => handleAddRole(role.id)}
                      disabled={assignLoading}
                    >
                      <RoleBadge name={role.name} color={role.color} />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <p
        className={`profile-bio ${profileData.bio && profileData.bio.length > 200 ? "profile-bio-clickable" : ""}`}
        onClick={() => {
          if (profileData.bio && profileData.bio.length > 200) {
            setShowBioModal(true);
          }
        }}
        title={profileData.bio && profileData.bio.length > 200 ? "Apasă pentru a citi biografia completă" : undefined}
      >
        {profileData.bio
          ? (profileData.bio.length > 200 ? `${profileData.bio.substring(0, 200)}...` : profileData.bio)
          : "No bio available"
        }
      </p>

      <StandardPopup
        isOpen={showBioModal}
        buttonsEnabled={false}
        onClose={() => setShowBioModal(false)}
        message={profileData.bio}
      />

      <div className="profile-details">
        <p><strong>Email:</strong> {profileData.email}</p>
        <p><strong>Phone:</strong> {profileData.phone || 'N/A'}</p>
        <p><strong>City:</strong> {profileData.oras || 'N/A'}</p>

        {profileData.show_birthdate && profileData.birthdate && (
          <p><strong>Birthday:</strong> {new Date(profileData.birthdate).toLocaleDateString()}</p>
        )}

        {profileData.org_join_date && (
          <p className="profile-member-since">
            Member since: {new Date(profileData.org_join_date).getFullYear()}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProfileCard;